import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { DollarSign, Camera, Network, Shield, Wifi, Wrench, ChevronDown, ChevronUp, Phone, AlertTriangle, Search, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import laborPrices from '../data/laborPrices.json';

// Category icons mapping
const categoryIcons = {
  'ACCESS CONTROL': Shield,
  'CCTV / SECURITY': Camera,
  'CCTV/SECURITY': Camera,
  'FIBER OPTIC': Network,
  'FIBER OPTICS': Network,
  'STRUCTURED CABLING': Wrench,
  'NETWORKING & WIFI': Wifi,
  'NETWORKING / WIFI / RF': Wifi,
  'SITE SURVEY': Search,
  'ENGINEERING / PM': Wrench,
  'DATA CENTER / MDF / IDF': Network,
  'UNDERGROUND / PATHWAYS / LIFT': Wrench,
  'MULTIPLIERS / CONDITIONS': Filter,
  'SERVICE / MAINTENANCE': Wrench,
};

// Category colors mapping
const categoryColors = {
  'ACCESS CONTROL': 'bg-purple-500',
  'CCTV / SECURITY': 'bg-blue-500',
  'CCTV/SECURITY': 'bg-blue-500',
  'FIBER OPTIC': 'bg-orange-500',
  'FIBER OPTICS': 'bg-orange-500',
  'STRUCTURED CABLING': 'bg-green-500',
  'NETWORKING & WIFI': 'bg-cyan-500',
  'NETWORKING / WIFI / RF': 'bg-cyan-500',
  'SITE SURVEY': 'bg-yellow-500',
  'ENGINEERING / PM': 'bg-indigo-500',
  'DATA CENTER / MDF / IDF': 'bg-gray-700',
  'UNDERGROUND / PATHWAYS / LIFT': 'bg-amber-600',
  'MULTIPLIERS / CONDITIONS': 'bg-rose-500',
  'SERVICE / MAINTENANCE': 'bg-teal-500',
};

const Pricing = () => {
  const { language } = useLanguage();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceData, setPriceData] = useState(null);

  useEffect(() => {
    // Load price data from JSON
    setPriceData(laborPrices);
  }, []);

  // Group items by category
  const groupedItems = priceData?.items?.reduce((acc, item) => {
    const cat = item.category;
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(item);
    return acc;
  }, {}) || {};

  // Filter items by search term
  const filterItems = (items) => {
    if (!searchTerm) return items;
    return items.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name_es.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const toggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const formatPrice = (min, max) => {
    if (min === max || !max) {
      return `$${min.toLocaleString()}`;
    }
    return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
  };

  if (!priceData) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#003366] to-[#004488] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <DollarSign className="w-16 h-16 mx-auto mb-4 text-[#FF6B00]" />
          <h1 className="text-4xl font-bold mb-4">
            {language === 'es' ? 'Lista de Precios - Mano de Obra' : 'Price List - Labor Only'}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {language === 'es' 
              ? `${priceData.total_items} servicios disponibles. Actualizado ${priceData.updated}.`
              : `${priceData.total_items} services available. Updated ${priceData.updated}.`}
          </p>
        </div>
      </section>

      {/* PROMINENT DISCLAIMER - LABOR ONLY */}
      <section className="bg-red-600 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3">
            <AlertTriangle className="w-6 h-6 flex-shrink-0" />
            <p className="text-center font-bold text-lg">
              {language === 'es' 
                ? '⚠️ SOLO MANO DE OBRA - Materiales NO incluidos. Estos son precios estimados, NO finales.'
                : '⚠️ LABOR ONLY - Materials NOT included. These are estimated prices, NOT final.'}
            </p>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={language === 'es' ? 'Buscar servicio...' : 'Search service...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
            data-testid="pricing-search"
          />
        </div>
      </section>

      {/* Pricing Categories */}
      <section className="pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="space-y-4">
          {Object.keys(groupedItems).sort().map((category) => {
            const filteredItems = filterItems(groupedItems[category]);
            if (filteredItems.length === 0) return null;
            
            const IconComponent = categoryIcons[category] || Wrench;
            const colorClass = categoryColors[category] || 'bg-gray-500';
            
            return (
              <div key={category} className="bg-white rounded-xl shadow-md overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  data-testid={`pricing-category-${category.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`${colorClass} p-3 rounded-lg`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h2 className="text-xl font-semibold text-gray-900">{category}</h2>
                      <p className="text-sm text-gray-500">{filteredItems.length} {language === 'es' ? 'servicios' : 'services'}</p>
                    </div>
                  </div>
                  {expandedCategory === category ? (
                    <ChevronUp className="w-6 h-6 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-gray-500" />
                  )}
                </button>

                {/* Category Prices */}
                {expandedCategory === category && (
                  <div className="px-6 pb-6">
                    <div className="border-t pt-4">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-sm text-gray-500 border-b">
                            <th className="pb-3 font-medium">{language === 'es' ? 'Servicio' : 'Service'}</th>
                            <th className="pb-3 font-medium text-center">{language === 'es' ? 'Unidad' : 'Unit'}</th>
                            <th className="pb-3 font-medium text-right">{language === 'es' ? 'Rango de Precio' : 'Price Range'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredItems.map((item, index) => (
                            <tr key={index} className="border-b last:border-b-0 hover:bg-gray-50">
                              <td className="py-3 text-gray-700">
                                <div className="font-medium">{language === 'es' ? item.name_es : item.name}</div>
                                {language === 'es' && item.name !== item.name_es && (
                                  <div className="text-xs text-gray-400">{item.name}</div>
                                )}
                              </td>
                              <td className="py-3 text-center">
                                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                  {language === 'es' ? item.unit_es : item.unit}
                                </span>
                              </td>
                              <td className="py-3 text-right">
                                <span className="font-semibold text-[#003366]">
                                  {formatPrice(item.price_min, item.price_max)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Detailed Disclaimer */}
        <div className="mt-8 bg-amber-50 border-2 border-amber-400 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-amber-800 text-lg mb-3">
                {language === 'es' ? '⚠️ AVISO IMPORTANTE - SOLO MANO DE OBRA' : '⚠️ IMPORTANT NOTICE - LABOR ONLY'}
              </h3>
              <ul className="text-amber-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="font-bold">1.</span>
                  <span>{language === 'es' 
                    ? 'Estos precios son SOLO de mano de obra. Los MATERIALES se cotizan por separado.'
                    : 'These prices are for LABOR ONLY. MATERIALS are quoted separately.'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">2.</span>
                  <span>{language === 'es' 
                    ? 'Estos son precios ESTIMADOS, no finales. El precio final se determina después de una visita técnica (Site Survey).'
                    : 'These are ESTIMATED prices, not final. Final pricing is determined after a Site Survey.'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">3.</span>
                  <span>{language === 'es' 
                    ? 'Los precios pueden variar según la complejidad del proyecto, altura, condiciones del sitio y otros factores.'
                    : 'Prices may vary based on project complexity, height, site conditions, and other factors.'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">4.</span>
                  <span>{language === 'es' 
                    ? 'El costo del Site Survey es 100% acreditable si nos contrata para el proyecto.'
                    : 'Site Survey cost is 100% creditable if you hire us for the project.'}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-[#FF6B00] to-[#FF8533] rounded-xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">
            {language === 'es' ? '¿Necesita una cotización personalizada?' : 'Need a custom quote?'}
          </h3>
          <p className="mb-6 text-white/90">
            {language === 'es' 
              ? 'Contáctenos para recibir una cotización detallada incluyendo materiales para su proyecto específico.'
              : 'Contact us to receive a detailed quote including materials for your specific project.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button 
                size="lg" 
                className="bg-white text-[#FF6B00] hover:bg-gray-100"
                data-testid="pricing-quote-btn"
              >
                {language === 'es' ? 'Solicitar Cotización' : 'Request Quote'}
              </Button>
            </Link>
            <a href="tel:+17869813760">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                data-testid="pricing-call-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                {language === 'es' ? 'Llamar Ahora' : 'Call Now'}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
